import { Pype, PypeConfig } from '../xstate/prostudio.api-service.types';

/**
 * The `config` object the channel forms are built around. It used to come from
 * the v3 `/configuration/pype/{id}` endpoint; it is now read off the pype itself
 * via `agent_routing_config` (backend#1256 exposed it on the Hasura action
 * types). Same shape, one less request.
 */
type IntegrationConfig = PypeConfig['config'];

/** Mirrors pype_api's `render_bot_retry/1` fallbacks. */
export const DEFAULT_BOT_RETRY = { attempts: 3, timeout: 35_000 };

export const EMPTY_INTEGRATION_CONFIG: IntegrationConfig = {
  integration_headers: { account_id: '', inbox_id: '' },
  integration_name: '',
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string =>
  typeof value === 'string' ? value : '';

/**
 * `agent_routing_config` is free-form JSON on the pype record — each platform
 * declares its own properties via the agent-routing schemas — so the same
 * information appears under different keys depending on who wrote it:
 *
 *  - the integration name is `integrationName` when written from the platform
 *    schemas (`/contactCenter/v2/integrations` names it that, and the connector
 *    modal follows), but `integration_name` in configs written by the legacy v3
 *    endpoint. Some rows carry both.
 *  - the contact-centre headers are sometimes nested under `integration_headers`
 *    and sometimes flat at the top level of the config.
 *
 * Read both shapes: missing the camelCase spelling makes every external
 * connector look like a bare `external_agent` with no platform.
 */
export const toIntegrationConfig = (raw: unknown): IntegrationConfig => {
  if (!isObject(raw)) {
    return EMPTY_INTEGRATION_CONFIG;
  }
  const nested = isObject(raw.integration_headers)
    ? raw.integration_headers
    : {};
  return {
    integration_headers: {
      account_id: str(nested.account_id) || str(raw.account_id),
      inbox_id: str(nested.inbox_id) || str(raw.inbox_id),
    },
    integration_name:
      str(raw.integrationName) ||
      str(raw.integration_name) ||
      str(nested.integrationName),
  };
};

/**
 * The connector's config exactly as stored, for round-tripping onto a channel.
 * Binding copies this verbatim so platform-specific keys (Quiq/Genesys tokens,
 * custom headers) survive — the old v3 `IntegrationHeadersInput` could only
 * carry `account_id`/`inbox_id` and silently dropped everything else.
 */
export const toRoutingConfig = (
  raw: unknown
): Record<string, unknown> | null => (isObject(raw) ? raw : null);

/**
 * The `agent_routing_config` blob for an external connector, in the shape the
 * legacy Console UI reads back from `GET /v3/configuration/pype/{id}` — it looks
 * the connector up by `config.integration_name` and renders its credentials from
 * `config.integration_headers`, so a config carrying only the flat schema fields
 * shows up there as an `external_agent` with no platform and empty inputs.
 *
 * Nothing downstream derives those keys: the backend copies `settings` verbatim
 * onto every bound pype as `agent_routing_config`, so the duplication has to be
 * written here.
 *
 * `integration_headers` is rebuilt from the current values on every save rather
 * than merged over the stored copy — otherwise editing a credential would leave
 * the previous value behind in the nested object. Nested values are skipped;
 * the only one is the previous `integration_headers` itself.
 *
 * `integrationName` is repeated inside the headers because the legacy v3 PATCH
 * transform writes it there too (camelCase among snake_case siblings, matching
 * the Hasura Kriti template).
 */
export const toExternalRoutingConfig = (
  config: Record<string, unknown>,
  integrationName: string
): Record<string, unknown> => {
  const headers = Object.fromEntries(
    Object.entries(config).filter(
      ([key, value]) =>
        key !== 'integrationName' &&
        key !== 'integration_name' &&
        key !== 'integration_headers' &&
        (typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean')
    )
  );
  return {
    ...config,
    integrationName,
    integration_name: integrationName,
    integration_headers: { ...headers, integrationName },
  };
};

export const toBotRetry = (
  raw: Pype['bot_retry'] | null | undefined
): { attempts: number; timeout: number } => ({
  attempts:
    typeof raw?.attempts === 'number'
      ? raw.attempts
      : DEFAULT_BOT_RETRY.attempts,
  timeout:
    typeof raw?.timeout === 'number' ? raw.timeout : DEFAULT_BOT_RETRY.timeout,
});

/**
 * Projects the routing fields the V6 pype list now returns into the shape the
 * rest of the app consumes (`config` / `bot_retry`), replacing the per-pype v3
 * `loadPypeConfig` round-trip.
 */
export const withRoutingConfig = (pype: Pype): Pype => ({
  ...pype,
  config: toIntegrationConfig(pype.agent_routing_config),
  bot_retry: toBotRetry(pype.bot_retry),
});

/**
 * The secondary, greyed-out line next to a platform's name — what identifies
 * *this* connection among others on the same platform (per Figma:
 * "Account ID: 142 Inbox ID 1111", "hbomax.my.salesforce.com").
 *
 * Which key carries that identity is platform-specific, so this checks the known
 * ones in order of specificity and returns '' when none is present rather than
 * inventing a label. Callers should render nothing for ''.
 */
export const describeConnection = (raw: unknown): string => {
  if (!isObject(raw)) {
    return '';
  }
  const { integration_headers: headers } = toIntegrationConfig(raw);
  if (headers.account_id || headers.inbox_id) {
    return [
      headers.account_id && `Account ID: ${headers.account_id}`,
      headers.inbox_id && `Inbox ID ${headers.inbox_id}`,
    ]
      .filter(Boolean)
      .join(' ');
  }
  // Salesforce/Genesys/Quiq identify a connection by the instance they point at.
  return (
    str(raw.instanceUrl) ||
    str(raw.instance_url) ||
    str(raw.liveagent_host) ||
    str(raw.freshchat_host) ||
    ''
  );
};

// `routingIdentity` / `pypeRoutingIdentity` used to live here. They reconstructed
// "which connector is this channel on" by comparing routing values, because the
// binding was a copy of the connector's config with no id stored anywhere.
// backend#1262 added the real join row (`pype_connection_pype`), so the binding is
// read directly now and the value matching — along with its "two identically
// configured connectors are indistinguishable" caveat — is gone.
